/**
 * Hook for performing tag CRUD operations
 * @remarks
 * This hook provides Apollo-based create, update, and delete operations for kanban tags.
 * It wraps the generated Apollo mutations with a cleaner interface and handles refetch logic
 * to keep the tag list up to date.
 *
 * Note: This hook handles tag CRUD but not player-tag relationships.
 * For adding/removing tags from players, use useTagMutations instead.
 * @example
 * ```tsx
 * const {
 *   createTag,
 *   updateTag,
 *   deleteTag,
 *   isCreating,
 *   isUpdating,
 *   isDeleting,
 *   error
 * } = useTagCrud(boardId);
 *
 * // Create a new tag
 * const newTag = await createTag('Important', '#FF0000');
 *
 * // Update a tag name
 * await updateTag('tag-123', { name: 'Very Important' });
 *
 * // Delete a tag
 * await deleteTag('tag-123');
 * ```
 */

import type { ApolloError } from "@apollo/client";
import { useCallback, useMemo } from "react";

import type { Tag } from "../types/types";

import {
  ListKanbanTagsDocument,
  useCreateKanbanTagMutation,
  useDeleteKanbanTagMutation,
  useUpdateKanbanTagMutation,
} from "@/generated/graphql";

/**
 * Return type for useTagCrud hook
 */
interface UseTagCrudReturn {
  /**
   * Create a new tag
   * @param name - Tag name
   * @param color - Tag color (hex code, frontend-only for now)
   * @throws {Error} if creation fails
   * @returns The created tag
   */
  createTag: (name: string, color?: string) => Promise<Tag>;

  /**
   * Update an existing tag
   * @param tagId - Tag ID
   * @param updates - Fields to update
   * @throws {Error} if update fails
   */
  updateTag: (tagId: string, updates: { name?: string }) => Promise<void>;

  /**
   * Delete a tag
   * @param tagId - Tag ID
   * @throws {Error} if deletion fails
   */
  deleteTag: (tagId: string) => Promise<void>;

  /**
   * Loading state for create operation
   */
  isCreating: boolean;

  /**
   * Loading state for update operation
   */
  isUpdating: boolean;

  /**
   * Loading state for delete operation
   */
  isDeleting: boolean;

  /**
   * Combined error from any operation
   */
  error: ApolloError | undefined;
}

/**
 * Hook for performing tag CRUD operations using Apollo Client
 * @param _boardId - The kanban board ID (unused for now, but kept for future board-specific tags)
 * @returns Object containing CRUD functions and loading/error states
 */
export function useTagCrud(_boardId: string): UseTagCrudReturn {
  // Setup mutation hooks
  const [createTagMutation, { loading: isCreating, error: createError }] =
    useCreateKanbanTagMutation();

  const [updateTagMutation, { loading: isUpdating, error: updateError }] =
    useUpdateKanbanTagMutation();

  const [deleteTagMutation, { loading: isDeleting, error: deleteError }] =
    useDeleteKanbanTagMutation();

  // Combine errors from all mutations
  const error = useMemo(
    () => createError || updateError || deleteError,
    [createError, updateError, deleteError]
  );

  /**
   * Create a new tag
   */
  const createTag = useCallback(
    async (name: string, color?: string): Promise<Tag> => {
      const result = await createTagMutation({
        variables: {
          input: {
            name,
          },
        },
        refetchQueries: [
          {
            query: ListKanbanTagsDocument,
            variables: { first: 100 },
          },
        ],
        awaitRefetchQueries: true,
      });

      const createdTag = result.data?.createKanbanTag;

      if (!createdTag) {
        throw new Error("Failed to create tag");
      }

      // Return tag in frontend format (with optional color for UI compatibility)
      return {
        id: createdTag.id,
        name: createdTag.name,
        cardCount: createdTag.cardCount,
        createdAt: createdTag.createdAt,
        updatedAt: createdTag.updatedAt,
        color: color || "#3B82F6", // Default blue color
        __typename: "KanbanTag" as const,
      };
    },
    [createTagMutation]
  );

  /**
   * Update an existing tag
   */
  const updateTag = useCallback(
    async (tagId: string, updates: { name?: string }): Promise<void> => {
      await updateTagMutation({
        variables: {
          id: tagId,
          input: {
            name: updates.name,
          },
        },
        refetchQueries: [
          {
            query: ListKanbanTagsDocument,
            variables: { first: 100 },
          },
        ],
        awaitRefetchQueries: true,
      });
    },
    [updateTagMutation]
  );

  /**
   * Delete a tag
   */
  const deleteTag = useCallback(
    async (tagId: string): Promise<void> => {
      await deleteTagMutation({
        variables: {
          id: tagId,
        },
        refetchQueries: [
          {
            query: ListKanbanTagsDocument,
            variables: { first: 100 },
          },
        ],
        awaitRefetchQueries: true,
      });
    },
    [deleteTagMutation]
  );

  return {
    createTag,
    updateTag,
    deleteTag,
    isCreating,
    isUpdating,
    isDeleting,
    error,
  };
}
